import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  type ConfirmationResult,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut,
} from "firebase/auth";
import { firebaseAuth } from "../config/firebase";

type RoleValue = "supplier" | "customer" | "expert" | "admin";

type SignupPayload = {
  email: string;
  username: string;
  phone: string;
  password: string;
  confirmPassword: string;
  role: RoleValue;
};

const MAX_CODE_ATTEMPTS = 3;
const RECAPTCHA_CONTAINER_ID = "signup-phone-recaptcha";
const PHONE_VERIFICATION_ENABLED =
  process.env.REACT_APP_PHONE_VERIFICATION_ENABLED === "true";

const SIGNUP_ERROR_MESSAGES: Record<string, string> = {
  EMAIL_ALREADY_EXISTS:
    "Cette adresse email est deja utilisee. Connectez-vous ou utilisez une autre adresse.",
  USERNAME_ALREADY_EXISTS:
    "Ce nom d'utilisateur est deja pris. Essayez un autre nom.",
  MISSING_REQUIRED_FIELDS: "Veuillez remplir tous les champs obligatoires.",
  PASSWORDS_DO_NOT_MATCH: "Les mots de passe ne correspondent pas.",
  INVALID_ROLE: "Veuillez choisir un role valide.",
  INVALID_PHONE_NUMBER: "Numero invalide. Exemple: 0555123456 ou +213555123456.",
  PHONE_VERIFICATION_REQUIRED: "La verification du telephone est requise.",
  PHONE_TOKEN_REQUIRED: "La verification du telephone n'a pas ete finalisee.",
  PHONE_NUMBER_MISMATCH: "Le numero saisi ne correspond pas au numero verifie.",
  EMAIL_CONFIRMATION_SETUP_FAILED:
    "La confirmation email n'est pas encore configuree sur le serveur.",
  EMAIL_CONFIRMATION_TOKEN_FAILED:
    "Le lien de confirmation n'a pas pu etre genere. Contactez l'administrateur.",
  SIGNUP_PROFILE_CREATION_FAILED:
    "Le compte a ete cree, mais le profil n'a pas pu etre initialise. Contactez l'administrateur.",
};

export function useSignupForm(params: {
  backendUrl?: string;
  translate: (k: string) => string;
  onSuccess: () => void;
}) {
  const { backendUrl, translate } = params;

  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<RoleValue>("customer");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreePolicy, setAgreePolicy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationPhone, setVerificationPhone] = useState("");
  const [verificationMessage, setVerificationMessage] = useState("");
  const [attemptsLeft, setAttemptsLeft] = useState(MAX_CODE_ATTEMPTS);
  const [isVerificationStep, setIsVerificationStep] = useState(false);

  const signupButtonRef = useRef<HTMLButtonElement>(null);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const recaptchaWidgetIdRef = useRef<number | null>(null);
  const confirmationResultRef = useRef<ConfirmationResult | null>(null);
  const pendingSignupRef = useRef<SignupPayload | null>(null);

  useEffect(() => {
    return () => {
      clearRecaptchaVerifier();
      void signOut(firebaseAuth).catch(() => undefined);
    };
  }, []);

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const normalizePhone = (value: string) => {
    let normalized = value.trim().replace(/[^\d+]/g, "");

    if (normalized.startsWith("00")) normalized = `+${normalized.slice(2)}`;
    if (/^0[567]\d{8}$/.test(normalized)) normalized = `+213${normalized.slice(1)}`;
    if (/^[567]\d{8}$/.test(normalized)) normalized = `+213${normalized}`;
    if (/^213[567]\d{8}$/.test(normalized)) normalized = `+${normalized}`;

    return /^\+[1-9]\d{7,14}$/.test(normalized) ? normalized : "";
  };

  const resetForm = () => {
    setEmail("");
    setUsername("");
    setPhone("");
    setPassword("");
    setConfirmPassword("");
    setAgreePolicy(false);
  };

  const validate = () => {
    setAlertMessage("");

    const trimmedEmail = email.trim();
    const trimmedUsername = username.trim() || trimmedEmail;
    const normalizedPhone = normalizePhone(phone);

    if (!trimmedEmail) return "Veuillez saisir votre adresse email.";
    if (!isValidEmail(trimmedEmail)) return "Adresse email invalide. Verifiez le format, par exemple nom@domaine.com.";
    if (!trimmedUsername) return "Veuillez saisir un nom d'utilisateur.";
    if (!normalizedPhone) return SIGNUP_ERROR_MESSAGES.INVALID_PHONE_NUMBER;
    if (!password) return "Veuillez saisir un mot de passe.";
    if (password.length < 6) return "Le mot de passe doit contenir au moins 6 caracteres.";
    if (confirmPassword !== password) return SIGNUP_ERROR_MESSAGES.PASSWORDS_DO_NOT_MATCH;
    if (!agreePolicy) return "Veuillez accepter la politique de confidentialite et les conditions.";

    return "";
  };

  const buildSignupPayload = (): SignupPayload | null => {
    const normalizedPhone = normalizePhone(phone);

    if (!normalizedPhone) {
      setAlertMessage(SIGNUP_ERROR_MESSAGES.INVALID_PHONE_NUMBER);
      return null;
    }

    return {
      email: email.trim(),
      username: username.trim() || email.trim(),
      phone: normalizedPhone,
      password,
      confirmPassword,
      role,
    };
  };

  const clearRecaptchaVerifier = () => {
    if (!recaptchaVerifierRef.current) return;

    try {
      recaptchaVerifierRef.current.clear();
    } catch (error) {
      console.error("reCAPTCHA clear error", error);
    } finally {
      recaptchaVerifierRef.current = null;
      recaptchaWidgetIdRef.current = null;
      const container = document.getElementById(RECAPTCHA_CONTAINER_ID);
      if (container) container.innerHTML = "";
    }
  };

  const resetRecaptchaWidget = () => {
    if (recaptchaWidgetIdRef.current === null) return;

    const grecaptcha = (
      window as Window & { grecaptcha?: { reset: (widgetId?: number) => void } }
    ).grecaptcha;

    try {
      grecaptcha?.reset(recaptchaWidgetIdRef.current);
    } catch (error) {
      console.error("reCAPTCHA reset error", error);
    }
  };

  const ensureRecaptchaVerifier = async () => {
    let container = document.getElementById(RECAPTCHA_CONTAINER_ID);

    if (!container) {
      container = document.createElement("div");
      container.id = RECAPTCHA_CONTAINER_ID;
      container.style.position = "absolute";
      container.style.width = "1px";
      container.style.height = "1px";
      container.style.overflow = "hidden";
      container.style.opacity = "0";
      container.style.pointerEvents = "none";
      document.body.appendChild(container);
    }

    if (!recaptchaVerifierRef.current) {
      const verifier = new RecaptchaVerifier(firebaseAuth, RECAPTCHA_CONTAINER_ID, {
        size: "invisible",
      });

      recaptchaVerifierRef.current = verifier;
      recaptchaWidgetIdRef.current = await verifier.render();
      return verifier;
    }

    if (recaptchaWidgetIdRef.current === null) {
      recaptchaWidgetIdRef.current = await recaptchaVerifierRef.current.render();
    } else {
      resetRecaptchaWidget();
    }

    return recaptchaVerifierRef.current;
  };

  const resetVerificationState = (clearVerifier = false) => {
    confirmationResultRef.current = null;
    pendingSignupRef.current = null;
    setVerificationCode("");
    setVerificationPhone("");
    setVerificationMessage("");
    setAttemptsLeft(MAX_CODE_ATTEMPTS);
    setIsVerificationStep(false);

    if (clearVerifier) clearRecaptchaVerifier();
    else resetRecaptchaWidget();
  };

  const createAccount = async (signupPayload: SignupPayload, firebaseIdToken?: string) => {
    const response = await axios.post(
      `${backendUrl}/api/signup`,
      firebaseIdToken ? { ...signupPayload, firebaseIdToken } : signupPayload,
      { withCredentials: true }
    );

    resetForm();
    setAlertMessage(
      response.data?.email_sent
        ? "Compte cree avec succes. Verifiez votre email pour activer votre compte."
        : "Compte cree. L'email de confirmation n'a pas pu etre envoye automatiquement."
    );
  };

  const getSignupErrorMessage = (error: any) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const data = error.response?.data as { code?: string; message?: string } | undefined;
      const code = data?.code;
      const serverMessage = data?.message;

      if (code && SIGNUP_ERROR_MESSAGES[code]) return SIGNUP_ERROR_MESSAGES[code];
      if (serverMessage && SIGNUP_ERROR_MESSAGES[serverMessage]) {
        return SIGNUP_ERROR_MESSAGES[serverMessage];
      }

      if (serverMessage && !serverMessage.toLowerCase().includes("erreur serveur")) {
        return serverMessage;
      }

      if (status === 409) {
        return "Un compte existe deja avec ces informations. Connectez-vous ou utilisez un autre email.";
      }

      if (status === 400) {
        return "Certaines informations sont incorrectes. Verifiez le formulaire puis reessayez.";
      }

      if (status === 401 || status === 403) {
        return "Votre inscription ne peut pas etre validee pour le moment.";
      }

      if (!error.response) {
        return "Impossible de contacter le serveur. Verifiez votre connexion puis reessayez.";
      }

      if (status && status >= 500) {
        return "Le serveur n'a pas pu finaliser l'inscription. Verifiez la configuration email puis reessayez.";
      }
    }

    return "Inscription impossible pour le moment. Reessayez dans quelques instants.";
  };

  const getFirebaseRequestErrorMessage = (error: any) => {
    switch (error?.code) {
      case "auth/billing-not-enabled":
        return "La verification telephone est indisponible pour le moment. Contactez l'administrateur.";
      case "auth/invalid-phone-number":
        return SIGNUP_ERROR_MESSAGES.INVALID_PHONE_NUMBER;
      case "auth/operation-not-allowed":
        return "Firebase Phone Auth n'est pas active dans Firebase Authentication.";
      case "auth/unauthorized-domain":
        return "Ce domaine n'est pas autorise dans Firebase Authentication.";
      case "auth/too-many-requests":
        return translate("Too many verification requests. Please try again later.");
      default:
        return error?.message || translate("Phone verification failed. Please try again.");
    }
  };

  const requestVerificationCode = async () => {
    const errorMessage = validate();
    if (errorMessage) {
      setAlertMessage(errorMessage);
      return;
    }

    const signupPayload = buildSignupPayload();
    if (!signupPayload) return;

    try {
      setLoading(true);
      setAlertMessage("");

      if (!PHONE_VERIFICATION_ENABLED) {
        await createAccount(signupPayload);
        return;
      }

      await signOut(firebaseAuth).catch(() => undefined);
      resetVerificationState();

      const verifier = await ensureRecaptchaVerifier();
      const confirmationResult = await signInWithPhoneNumber(
        firebaseAuth,
        signupPayload.phone,
        verifier
      );

      confirmationResultRef.current = confirmationResult;
      pendingSignupRef.current = signupPayload;
      setVerificationPhone(signupPayload.phone);
      setAttemptsLeft(MAX_CODE_ATTEMPTS);
      setVerificationMessage(
        `${translate("A verification code has been sent to") || "A verification code has been sent to"} ${signupPayload.phone}.`
      );
      setIsVerificationStep(true);
    } catch (error: any) {
      resetVerificationState();
      setAlertMessage(
        PHONE_VERIFICATION_ENABLED
          ? getFirebaseRequestErrorMessage(error)
          : getSignupErrorMessage(error)
      );
      console.error("Phone verification request error", error);
    } finally {
      setLoading(false);
    }
  };

  const verifyCodeAndCreateAccount = async () => {
    const code = verificationCode.trim();
    const confirmationResult = confirmationResultRef.current;
    const signupPayload = pendingSignupRef.current;

    if (!confirmationResult || !signupPayload) {
      setAlertMessage(translate("Send a verification code first.") || "Send a verification code first.");
      return;
    }

    if (!code) {
      setAlertMessage(translate("Verification code is required") || "Verification code is required");
      return;
    }

    if (attemptsLeft <= 0) {
      setAlertMessage("Vous avez utilise toutes les tentatives. Demandez un nouveau code.");
      return;
    }

    try {
      setLoading(true);
      setAlertMessage("");

      const userCredential = await confirmationResult.confirm(code);
      const firebaseIdToken = await userCredential.user.getIdToken();

      await createAccount(signupPayload, firebaseIdToken);
      await signOut(firebaseAuth).catch(() => undefined);
      resetVerificationState(true);
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        setAlertMessage(getSignupErrorMessage(error));
        console.error("Signup after phone verification error", error);
        return;
      }

      const nextAttempts = Math.max(attemptsLeft - 1, 0);
      setAttemptsLeft(nextAttempts);
      setVerificationCode("");
      setAlertMessage(
        nextAttempts === 0
          ? "Vous avez utilise toutes les tentatives. Demandez un nouveau code."
          : `Code incorrect. Tentatives restantes: ${nextAttempts}.`
      );
      console.error("Phone verification confirm error", error);
    } finally {
      setLoading(false);
    }
  };

  const submit = () => {
    if (isVerificationStep) void verifyCodeAndCreateAccount();
    else void requestVerificationCode();
  };

  return {
    email,
    setEmail,
    username,
    setUsername,
    phone,
    setPhone,
    role,
    setRole,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    agreePolicy,
    setAgreePolicy,
    loading,
    alertMessage,
    setAlertMessage,
    signupButtonRef,
    prepareRecaptcha: ensureRecaptchaVerifier,
    submit,
    verificationCode,
    setVerificationCode,
    verificationPhone,
    verificationMessage,
    attemptsLeft,
    isVerificationStep,
    resendVerificationCode: requestVerificationCode,
    cancelVerification: () => {
      setAlertMessage("");
      resetVerificationState();
      void signOut(firebaseAuth).catch(() => undefined);
    },
    isFormLocked: loading || isVerificationStep,
  };
}
